<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, toRefs, reactive } from "vue";

// Pinia
import Store from "@store/dataProvider";
import { storeToRefs } from "pinia";

// Composables
import { useMessage } from "../../../composables/useMessage";

//types
import { IRole, IPermission } from "../../../types/user.type";
import { IAgents } from "./../../../types/agents.type";
import { ITemplateUI } from "../../../types/templatesUI.type";
import { IReinsurer } from "src/types/reinsurer.type";
import { IClient } from "src/types/client.type";

//repositories
import UserApi from "@repositories/user.repository";

//validations
import { insertHtmlValidations, validateFormat } from "./handleValidator";
import CountriesRepository from "../../../repositories/countries.repository";
import { ICountry } from "../../../types/countries.type";
import { ILanguage } from "../../../types/languages.type";
import { ICompany } from "../../../types/company.type";
import { ITypeUser } from "../../../types/typeUsers.type";
import { useI18n } from "vue-i18n";
import { applyTypeUserVisibility } from "../../../helpers/typeUserVisibility";

const { t } = useI18n();
const store = Store();
const { users }: any = storeToRefs(store);

const { messageHTML, showMessage } = useMessage();

const props = defineProps({
  agents: {
    type: Object as () => Array<IAgents>,
  },
  companies: {
    type: Object as () => Array<ICompany>,
  },
  reinsurers: {
    type: Object as () => Array<IReinsurer>,
  },
  clients: {
    type: Object as () => Array<IClient>,
  },
  typeUsers: {
    type: Object as () => Array<ITypeUser>,
  },

  templatesUI: {
    type: Object as () => Array<ITemplateUI>,
    default: () => ({
      templatesUI: {
        id: "",
        name: "",
      },
    }),
  },
  languages: {
    type: Object as () => Array<ILanguage>,
  },
});
const { agents, templatesUI, languages, companies, typeUsers, clients, reinsurers } = toRefs(props);

const emit = defineEmits<{
  (e: "closeModal"): void;
}>();

const userApiRepository = new UserApi();
const rolesData = ref<IRole[]>([]);
const countriesData = ref<ICountry[]>([]);
const selectedRoles = ref<IRole[]>([]);
const permissionsActive = ref<IPermission[]>([]);
const valueSelectAgents = ref<any>([]);
const valueSelectCountries = ref<any>([]);
const valueSelectCompanies = ref<any>([]);
const valueSelectTypeUser = ref<number | null>(null);
const refTableForm = ref<any>(null);
const passwordValidate: any = reactive({
  containNumber: /.*\d+.*/,
  containUppercase: /[A-Z]/,
  containLowercase: /[a-z]/,
  containSymbol: /.*[\W_].*/,
  minLength: /^.{8,}$/,
});

onMounted(async () => {
  const data = await userApiRepository.getRoles();
  const dataCountries = await CountriesRepository.getcountries();

  countriesData.value = dataCountries.data.countries.map((e: ICountry) => ({
    id: e.id,
    nombre: e.description,
  }));

  rolesData.value = data;
  insertHtmlValidations(passwordValidate);
});

const fields: any = ref([
  {
    key: "email",
    title: t("ajustes.usuarios.modalCreateUser.email_del_usuario"),
    type: "email",
    validations: {
      rules: {
        required: true,
        email: true,
      },
      messages: {
        required: t("global.es_obligatorio"),
        email: t("global.email_invalido"),
      },
    },
    placeholder: t("ajustes.usuarios.modalCreateUser.email_del_usuario"),
    value: null,
    cols: 6,
  },
  {
    key: "fullName",
    title: t("ajustes.usuarios.modalCreateUser.nombre_completo"),
    type: "text",
    validations: {
      rules: {
        required: true,
      },
      messages: {
        required: t("global.es_obligatorio"),
      },
    },
    placeholder: t("ajustes.usuarios.modalCreateUser.nombre_completo"),
    value: null,
    cols: 6,
  },
  {
    key: "urlDomain",
    title: t("ajustes.usuarios.modalCreateUser.url_dominio"),
    type: "text",
    placeholder: t("ajustes.usuarios.modalCreateUser.url_dominio"),
    value: null,
    cols: 6,
  },
  {
    key: "password",
    title: t("ajustes.usuarios.modalCreateUser.contrasenia"),
    type: "password",
    validations: {
      rules: {
        required: true,
        min: 8,
        verifyExpreRegular:
          /^(?=.*[!@#$%^&*()_+{}\[\]:;<>,.?~\\-])(?=.*[A-Z])(?=.*[a-z])(?=.*\d).+$/,
      },
      messages: {
        required: t("global.es_obligatorio"),
      },
    },
    placeholder: "Ejemplo.123",
    value: null,
    cols: 6,
    customClass: "inputs-password",
    formatValue: (e: any) => validateFormat(e, passwordValidate),
  },
  {
    key: "repeatPassword",
    title: t("ajustes.usuarios.modalCreateUser.repetir_contrasenia"),
    type: "password",
    validations: {
      rules: {
        required: true,
      },
      messages: {
        required: t("global.es_obligatorio"),
      },
    },
    placeholder: t("ajustes.usuarios.modalCreateUser.repetir_contrasenia"),
    value: null,
    cols: 6,
  },
  {
    key: "countries",
    title: t(
      "ajustes.usuarios.modalCreateUser.selecciona_los_paises_del_usuario"
    ),
    selectLabel: null,
    type: "select",
    placeholder: t("ajustes.usuarios.modalCreateUser.seleccionar_paises"),
    value: valueSelectCountries,
    cols: 6,
    options: countriesData,
    validations: {
      rules: {
        required: true,
      },
      messages: {
        required: t("global.es_obligatorio"),
      },
    },
    hidden: false,
    multiple: true,
    listenSelect: false,
    listenRemove: false,
  },
  {
    key: "typeUserId",
    title: t("typeUsers.key"),
    type: "select",
    placeholder: t("typeUsers.key"),
    value: valueSelectTypeUser,
    // label: 'type',
    customLabel: (e: any) => t(`typeUsers.${e.type}`),
    cols: 6,
    multiple: false,
    options: typeUsers?.value,
    formatValue: (e: ITypeUser) => e?.id,
    listenSelect: true,
  },
    {
    key: 'companyId',
    title: t('global.compania'),
    type: 'select',
    placeholder: t('global.compania'),
    selectLabel: null,
    multiple: false,
    cols: 6,
    options: companies?.value,
    formatValue: (e: ICompany) => e?.id,
    hidden: false,
  },
  //reasegurador aca lo dejo!!! debo llamar los reaseguradores al backend
  // si un agente puede tener muchas companias entonces un cliente debe tener una sola compania
  {
    key: 'reinsurerId',
    title: t('global.reasegurador'),
    label: 'name',
    type: 'select',
    placeholder: t('global.reasegurador'),
    selectLabel: null,
    multiple: false,
    cols: 6,
    options: reinsurers?.value,
    formatValue: (e: IReinsurer) => e?.id,
    hidden: true,
  },
  {
    key: 'agentId',
    customLabel: (e: any) => `${e.name} - [${e?.rfc || e?.rut || e?.nif || e?.cuit}]`,
    searchable: true,
    title: t(
      'ajustes.usuarios.modalUpdateUser.agente',
    ),
    type: 'select',
    placeholder: t(
      'ajustes.usuarios.modalUpdateUser.selecciona_uno_varios_ninguno',
    ),
    cols: 6,
    options: agents?.value,
    hidden: true,
    formatValue: (e: IAgents) => e?.id,
    multiple: false,
  },
  {
    key: 'clientId',
    label: 'tradeName',
    searchable: true,
    title: t(
      'ajustes.usuarios.modalUpdateUser.cliente',
    ),
    type: 'select',
    placeholder: t(
      'ajustes.usuarios.modalUpdateUser.selecciona_uno_varios_ninguno',
    ),
    cols: 6,
    options: clients?.value,
    formatValue: (e: IClient) => e?.id,
    hidden: true,
    multiple: false,
  },
  {
    key: "roles",
    title: t(
      "ajustes.usuarios.modalCreateUser.selecciona_los_roles_del_usuario"
    ),
    selectLabel: null,
    label: "title",
    type: "select",
    placeholder: t("ajustes.usuarios.modalCreateUser.seleccionar_roles"),
    value: selectedRoles,
    cols: 6,
    options: rolesData,
    validations: {
      rules: {
        required: true,
      },
      messages: {
        required: t("global.es_obligatorio"),
      },
    },
    hidden: false,
    multiple: true,
    listenSelect: true,
    listenRemove: true,
  },
  {
    key: "templateId",
    title: t("ajustes.usuarios.modalCreateUser.plantilla"),
    type: "select",
    placeholder: t("global.elije_una_opcion"),
    value: null,
    selectLabel: null,
    cols: 6,
    options: templatesUI?.value?.map((e: ITemplateUI) => ({
      nombre: e.name,
      id: e.id,
    })),
  },
  {
    key: "languageId",
    title: t("ajustes.usuarios.modalCreateUser.idioma"),
    type: "select",
    placeholder: t("global.elije_una_opcion"),
    value: null,
    selectLabel: null,
    cols: 6,
    options: languages?.value?.map((e: ILanguage) => ({
      nombre: e.language,
      id: e.id,
    })),
  },
]);

const handleSelect = async (e: any) => {
  if (e.input.key === 'typeUserId' && e.value?.id) {
    await applyTypeUserVisibility(
      e.value.id,
      fields.value,
    );
    return;
  }

  const respose = await refTableForm.value.getValues();

  valueSelectAgents.value = respose.items.agents;
  valueSelectCountries.value = respose.items.countries;
  valueSelectCompanies.value = respose.items.companies;

  if (selectedRoles.value.length === 0) {
    selectedRoles.value = e.value;
    const { data } = await userApiRepository.getPermissionsByRoleId(
      e.value[0].id
    );
    permissionsActive.value = data;
  } else {
    const differentObject = e.value.find(
      (itemA: any) =>
        !selectedRoles.value.some((itemB) => itemA.id === itemB.id)
    );

    if (differentObject) {
      selectedRoles.value = e.value;

      const { data } = await userApiRepository.getPermissionsByRoleId(
        differentObject.id
      );

      permissionsActive.value = [...permissionsActive.value, ...data];
      permissionsActive.value = permissionsActive.value.filter(
        (item, index, self) =>
          index === self.findIndex((obj) => obj.id === item.id)
      );
    }
  }
};

const handleRemove = async (e: any) => {
  selectedRoles.value = selectedRoles.value.filter((i) => i.id !== e.value.id);
  const respose = await refTableForm.value.getValues();

  valueSelectAgents.value = respose.items.agents;
  valueSelectCountries.value = respose.items.countries;
  valueSelectCompanies.value = respose.items.companies;

  if (!selectedRoles.value.length) {
    permissionsActive.value = [];
  }

  for (const role of selectedRoles.value) {
    const { data } = await userApiRepository.getPermissionsByRoleId(role.id);

    permissionsActive.value = data;
  }
};

const handleOnSubmit = async ({ items, isFormValid }: any) => {
  if (!isFormValid) return;
  if (items.password !== items.repeatPassword) {
    return showMessage(
      t("ajustes.usuarios.modalCreateUser.las_contrasenias_deben_ser_iguales"),
      "error"
    );
  } else {
    showMessage(null);
  }

  const newUser = {
    ...items,
    templateId: items.templateId ? items.templateId.id : null,
    languageId: items.languageId?.id ?? null,
  };

  try {
    const { data } = await userApiRepository.createUser(newUser);
    store.setProp({ key: "users", value: [...users.value, data] });
    emit("closeModal");
  } catch (error: any) {
    showMessage(error.message, "error");
  }
};

onBeforeUnmount(() => {
  fields.value.forEach((field: any) => {
    field.value = null;
  });
});
</script>

<template>
  <Form
    ref="refTableForm"
    :inputs="fields"
    @onSubmit="handleOnSubmit"
    @select="handleSelect"
    @remove="handleRemove"
  >
    <template #buttons>
      <p v-if="permissionsActive.length">
        {{ t("ajustes.usuarios.modalCreateUser.permisos_activos") }}
      </p>
      <ul v v-for="permission in permissionsActive" :key="permission.id">
        <li>{{ permission.descripcion }}</li>
      </ul>
      <div v-if="messageHTML" v-html="messageHTML" />
      <div class="d-flex">
        <CButton class="w-100 fs-6 p-2" background="background-blue">
          <template #content>
            <div>
              <span>{{ t("global.crear") }}</span>
            </div>
          </template>
        </CButton>
      </div>
    </template>
  </Form>
</template>
